<template>
  <div ref="div">
    <canvas
      class="plan-canvas"
      ref="plan"
      @mousedown="onCanvasMouseDown"
      @mouseup="onCanvasMouseUp"
      @mousemove="onCanvasMouseMove"
    >
    </canvas>
    <div style="display: none">
      <img ref="source" />
    </div>
  </div>
</template>

<script>
const HitTestResult = {
  None: 0,
  Hit: 9,
  NW: 1,
  NE: 2,
  N: 3,
  SW: 4,
  SE: 5,
  S: 6,
  W: 7,
  E: 8,
};
const HitTestCursor = {
  [HitTestResult.None]: "auto",
  [HitTestResult.Hit]: "move",
  [HitTestResult.NW]: "nw-resize",
  [HitTestResult.NE]: "ne-resize",
  [HitTestResult.N]: "n-resize",
  [HitTestResult.SW]: "sw-resize",
  [HitTestResult.SE]: "se-resize",
  [HitTestResult.S]: "s-resize",
  [HitTestResult.W]: "w-resize",
  [HitTestResult.E]: "e-resize",
};

export default {
  name: "PlanCanvas",
  props: {
    shapes: null,
    config: null,
    canvasWidth: 0,
    canvasHeight: 0,
    editMode: null,
  },
  data: () => ({
    mousePos: { x: 0, y: 0 },
    hoveredShape: null,
    drag: {
      dragging: false,
      hit: HitTestResult.None,
      mousePos: null,
      mouseDelta: null,
      shapeRef: null,
      shapeCopy: null,
      setCursor: false,
      handlers: null,
    },
    devicePixelRatio: 0,
    timestamps: {
      lastDraw: 0,
      lastMouseMove: 0,
    },
    drawImage: true,
  }),
  methods: {
    addDevicePixelRatioListener() {
      const listener = () => {
        matchMedia(
          `(resolution: ${window.devicePixelRatio}dppx)`
        ).addEventListener("change", listener, {
          once: true,
        });
        this.requestCanvasFrame();
      };
      listener();
    },
    requestCanvasFrame() {
      if (this.$refs.plan) window.requestAnimationFrame(this.draw);
    },
    ensureCanvasResolution(ctx) {
      const canvas = this.$refs.plan;
      const scale = window.devicePixelRatio;

      // if (this.devicePixelRatio != scale) {
      canvas.width = this.canvasWidth * scale;
      canvas.height = this.canvasHeight * scale;
      canvas.style.width = this.canvasWidth + "px";
      canvas.style.height = this.canvasHeight + "px";
      this.devicePixelRatio = scale;
      // }

      ctx.resetTransform();
      ctx.scale(scale, scale);
    },
    clearCanvas(ctx) {
      ctx.clearRect(0, 0, this.canvasWidth, this.canvasHeight);

      ctx.fillStyle = this.config.style.canvas.fillStyle;
      ctx.roundRect(
        0,
        0,
        this.canvasWidth,
        this.canvasHeight,
        this.config.style.canvas.borderRadius
      );
      ctx.fill();
    },
    draw(timestamp) {
      if (!this.shouldDrawFrame(timestamp)) return;
      const ctx = this.$refs.plan.getContext("2d");
      this.ensureCanvasResolution(ctx);
      this.clearCanvas(ctx);
      this.drawBackgroundImage(ctx);
      this.drawDots(ctx);
      this.drawShapes(ctx);
    },

    drawBackgroundImage(ctx) {
      if (!this.drawImage) return;
      ctx.globalAlpha = 0.5;
      ctx.drawImage(
        this.$refs.source,
        0,
        0,
        this.canvasWidth,
        this.canvasHeight
      );
      ctx.globalAlpha = 1;
    },
    drawDots(ctx) {
      ctx.fillStyle = this.config.style.dot.fillStyle;

      for (
        let i = this.config.sizing.spaceBetweenDots;
        i < this.canvasHeight;
        i += this.config.sizing.spaceBetweenDots
      ) {
        for (
          let j = this.config.sizing.spaceBetweenDots;
          j < this.canvasWidth;
          j += this.config.sizing.spaceBetweenDots
        ) {
          ctx.fillRect(
            j - this.config.sizing.dotWidth / 2,
            i - this.config.sizing.dotWidth / 2,
            this.config.sizing.dotWidth,
            this.config.sizing.dotWidth
          );
        }
      }
    },
    drawShape(ctx, shape, hovered) {
      ctx.fillStyle = hovered
        ? this.config.style.shape.hoveredFillStyle
        : this.config.style.shape.fillStyle;
      ctx.strokeStyle = hovered
        ? this.config.style.shape.hoveredStrokeStyle
        : this.config.style.shape.strokeStyle;
      ctx.lineWidth = this.config.style.shape.lineWidth;
      ctx.setLineDash(this.config.style.shape.lineDashPattern);

      ctx.beginPath();
      ctx.roundRect(
        shape.x,
        shape.y,
        shape.w,
        shape.h,
        this.config.style.shape.borderRadius
      );
      ctx.fill();
      ctx.stroke();

      this.drawShapeTitle(ctx, shape);
    },
    drawShapeTitle(ctx, shape) {
      if (shape.w != 0 && shape.h != 0) {
        let x = Math.round(shape.x + this.config.style.shape.title.offsetX),
          y = Math.round(shape.y + this.config.style.shape.title.offsetY);
        if (shape.w < 0) x += shape.w;
        if (shape.h < 0) y += shape.h;

        ctx.fillStyle = this.config.style.shape.title.fillStyle;
        ctx.font = this.config.style.shape.title.font;

        ctx.fillText(shape.name, x, y);
      }
    },
    drawShapes(ctx) {
      for (const shape of this.shapes)
        this.drawShape(ctx, shape, shape == this.hoveredShape);
    },

    onCanvasMouseDown(e) {
      if (!this.editMode || e.button != 0) return;
      this.updateMousePosition(e);
      this.mouseDownInitiateDragging();
    },
    onCanvasMouseUp(e) {
      if (!this.editMode || e.button != 0) return;
      this.mouseUpHandleDragging();
    },
    onCanvasMouseMove(e) {
      if (!this.editMode || !this.shouldHandleMouseMove()) return;
      this.updateMousePosition(e);
      this.mouseMoveHandleCursor();
      this.mouseMoveHandleDragging();
    },

    mouseDownInitiateDragging() {
      const hit = this.shapesHitTest();

      if (hit) {
        this.drag.dragging = true;
        this.drag.hit = hit.result;
        this.drag.mousePos = structuredClone(this.mousePos);
        this.drag.mouseDelta = {
          x: hit.shape.x - this.mousePos.x,
          y: hit.shape.y - this.mousePos.y,
        };
        this.drag.shapeRef = hit.shape;
        this.drag.shapeCopy = structuredClone(hit.shape);
        this.drag.setCursor = true;

        // last shape is drawn first
        this.shapes.push(this.shapes.splice(hit.index, 1)[0]);
      }
    },
    mouseUpHandleDragging() {
      if (this.drag.dragging) {
        this.drag.dragging = false;
        this.drag.setCursor = false;
        this.normalizeDragShape();
      }
    },
    mouseMoveHandleCursor() {
      let cursor = "auto";
      const prevHoveredShape = this.hoveredShape;

      if (this.drag.dragging && this.drag.setCursor) {
        cursor = this.shapeHitTestCursor(this.drag.shapeRef);
        this.drag.setCursor = false;
        this.$refs.plan.style.cursor = cursor;
        this.hoveredShape = this.drag.shapeRef;
      } else if (!this.drag.dragging) {
        const hit = this.shapesHitTest();
        if (hit) cursor = HitTestCursor[hit.result];
        this.hoveredShape = hit ? hit.shape : null;
        this.$refs.plan.style.cursor = cursor;
      }
      if (prevHoveredShape != this.hoveredShape) this.requestCanvasFrame();
    },
    makeMouseMoveDragHandlers() {
      let handlers = {
        [HitTestResult.Hit]: () => {
          this.drag.shapeRef.x = this.snapCoordToDot(
            this.mousePos.x + this.drag.mouseDelta.x
          );
          this.drag.shapeRef.y = this.snapCoordToDot(
            this.mousePos.y + this.drag.mouseDelta.y
          );
        },
        [HitTestResult.N]: () => {
          const dt = this.drag.mousePos.y - this.mousePos.y;
          this.drag.shapeRef.y = this.snapCoordToDot(
            this.drag.shapeCopy.y - dt
          );
          this.drag.shapeRef.h = this.snapCoordToDot(
            this.drag.shapeCopy.h + dt - 0.5
          );
        },
        [HitTestResult.S]: () => {
          const dt = this.mousePos.y - this.drag.mousePos.y;
          this.drag.shapeRef.h = this.snapCoordToDot(
            this.drag.shapeCopy.h + dt + 0.5
          );
        },
        [HitTestResult.W]: () => {
          const dt = this.drag.mousePos.x - this.mousePos.x;
          this.drag.shapeRef.x = this.snapCoordToDot(
            this.drag.shapeCopy.x - dt
          );
          this.drag.shapeRef.w = this.snapCoordToDot(
            this.drag.shapeCopy.w + dt - 0.5
          );
        },
        [HitTestResult.E]: () => {
          const dt = this.mousePos.x - this.drag.mousePos.x;
          this.drag.shapeRef.w = this.snapCoordToDot(
            this.drag.shapeCopy.w + dt + 0.5
          );
        },
      };
      handlers[HitTestResult.NW] = () => {
        handlers[HitTestResult.N]();
        handlers[HitTestResult.W]();
      };
      handlers[HitTestResult.NE] = () => {
        handlers[HitTestResult.N]();
        handlers[HitTestResult.E]();
      };
      handlers[HitTestResult.SW] = () => {
        handlers[HitTestResult.S]();
        handlers[HitTestResult.W]();
      };
      handlers[HitTestResult.SE] = () => {
        handlers[HitTestResult.S]();
        handlers[HitTestResult.E]();
      };

      this.drag.handlers = handlers;
    },
    mouseMoveHandleDragging() {
      if (this.drag.dragging) {
        this.drag.handlers[this.drag.hit]();
        this.requestCanvasFrame();
      }
    },

    updateMousePosition(e) {
      const rect = this.$refs.plan.getBoundingClientRect();
      this.mousePos.x = e.clientX - rect.left;
      this.mousePos.y = e.clientY - rect.top;
    },
    normalizeDragShape() {
      if (this.drag.shapeRef.w < 0) {
        this.drag.shapeRef.x += this.drag.shapeRef.w;
        this.drag.shapeRef.w = -this.drag.shapeRef.w;
      }
      if (this.drag.shapeRef.h < 0) {
        this.drag.shapeRef.y += this.drag.shapeRef.h;
        this.drag.shapeRef.h = -this.drag.shapeRef.h;
      }
    },
    snapCoordToDot(coord) {
      return (
        Math.round(coord / this.config.sizing.spaceBetweenDots) *
        this.config.sizing.spaceBetweenDots
      );
    },
    shapeHitTest(shape) {
      const margin = this.config.hitTesting.margin;
      const padding = this.config.hitTesting.padding;

      const topBorder =
        this.mousePos.y > shape.y - margin &&
        this.mousePos.y < shape.y + padding;
      const leftBorder =
        this.mousePos.x > shape.x - margin &&
        this.mousePos.x < shape.x + padding;
      const bottomBorder =
        this.mousePos.y > shape.y + shape.h - padding &&
        this.mousePos.y < shape.y + shape.h + margin;
      const rightBorder =
        this.mousePos.x > shape.x + shape.w - padding &&
        this.mousePos.x < shape.x + shape.w + margin;

      const top = this.mousePos.y > shape.y;
      const left = this.mousePos.x > shape.x;
      const bottom = this.mousePos.y < shape.y + shape.h;
      const right = this.mousePos.x < shape.x + shape.w;

      if (topBorder && leftBorder) return HitTestResult.NW;
      else if (topBorder && rightBorder) return HitTestResult.NE;
      else if (topBorder && left && right) return HitTestResult.N;
      else if (bottomBorder && leftBorder) return HitTestResult.SW;
      else if (bottomBorder && rightBorder) return HitTestResult.SE;
      else if (bottomBorder && left && right) return HitTestResult.S;
      else if (leftBorder && top && bottom) return HitTestResult.W;
      else if (rightBorder && top && bottom) return HitTestResult.E;
      else if (left && top && right && bottom) return HitTestResult.Hit;
      return HitTestResult.None;
    },
    shapeHitTestCursor(shape) {
      return HitTestCursor[this.shapeHitTest(shape)];
    },
    shapesHitTest() {
      for (let i = 0; i < this.shapes.length; ++i) {
        const index = this.shapes.length - 1 - i;
        const shape = this.shapes[index];
        const hit = this.shapeHitTest(shape);

        if (hit != HitTestResult.None)
          return { result: hit, shape: shape, index: index };
      }
      return null;
    },

    shouldDrawFrame(timestamp) {
      if (this.config.frameLimiter.enabled) {
        const dt = timestamp - this.timestamps.lastDraw;
        if (dt < this.config.frameLimiter.interval) return false;
        this.timestamps.lastDraw = timestamp;
      }
      return true;
    },
    shouldHandleMouseMove() {
      const timestamp = performance.now();
      if (this.config.frameLimiter.enabled) {
        const dt = timestamp - this.timestamps.lastMouseMove;
        if (dt < this.config.frameLimiter.interval) return false;
        this.timestamps.lastMouseMove = timestamp;
      }
      return true;
    },

    dump() {
      console.log(JSON.stringify(this.shapes));
    },
    bringToFront(shape) {
      this.shapes.push(this.shapes.splice(this.shapes.indexOf(shape), 1)[0]);
      this.requestCanvasFrame();
    },
    resetCursor() {
      this.setCursor = false;
      this.$refs.plan.style.cursor = "auto";
    },
  },
  watch: {
    shapes() {
      this.requestCanvasFrame();
    },
  },
  mounted() {
    this.addDevicePixelRatioListener();
    this.makeMouseMoveDragHandlers();
    this.requestCanvasFrame();
  },
};
</script>

<style lang="scss" scoped>
.plan-canvas {
  filter: drop-shadow(0px 2px 2px #00000040);
}
</style>
